

























































import { Component, Mixins, Watch, Prop } from "vue-property-decorator";
import { mapState } from "vuex";

// Mixins
import RequestPromises from "@/mixins/requestPromises";
import BrandsStore from "@/mixins/brandsStore";

// Enums
import { ProductTypes, RelatedProductsTypes } from "@/data/enums/products";
import { SortableItems } from "@/data/enums/sortableItems";
import { DataModules } from "@/store/modules/data";

// Types
import { IState } from "@/store";
import { IProductCategory, ISelectedProduct } from "@/models/products";

@Component({
  computed: {
    ...mapState({
      rootCategories(state: IState, getters) {
        return getters[`data/listArray`]({
          storeModule: DataModules.CATALOGUE_CATEGORIES,
          scope: this.rootCategoriesScope
        });
      },
      rootCategoriesScope(state: IState, getters) {
        return getters[
          `data/${DataModules.CATALOGUE_CATEGORIES}/rootCategoriesScope`
        ](this.brandId, this.productTypes);
      }
    })
  }
})
export default class SelectCategories extends Mixins(
  RequestPromises,
  BrandsStore
) {
  // Getters
  public rootCategoriesScope!: string;
  public rootCategories!: IProductCategory[];

  // Props
  @Prop({
    default: () => [ProductTypes.SINGLE_PRODUCT, ProductTypes.PRODUCT_BUNDLE]
  })
  public productTypes!: ProductTypes[];

  @Prop({ required: true })
  public value!: ISelectedProduct[];

  @Prop({ default: true })
  public multipleSelect!: boolean;

  // Data
  public sortableData = [];
  public isLoading = true;
  public isReloading = false;
  public requestError = false;
  public selectedCategories: ISelectedProduct[] = this.value;

  // Methods
  public created() {
    if (!this.contentAvailable) {
      return;
    }

    this.isLoading = true;
    this.loadData();
  }

  public async loadData() {
    try {
      await this.getRootCategories();
      this.sortableData = this.mapCategories(this.rootCategories, [], 0);
      this.requestError = false;
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
      this.requestError = true;
    } finally {
      this.isLoading = false;
      this.isReloading = false;
    }
  }

  public reloadData() {
    this.isReloading = true;
    this.loadData();
  }

  public getRootCategories(forceReload = false) {
    return this.$store.dispatch(
      `data/${DataModules.CATALOGUE_CATEGORIES}/list`,
      {
        productTypes: this.productTypes,
        scope: this.rootCategoriesScope,
        params: {
          order: "order",
          limit: 0,
          "filter[level]": 1,
          brand_id: this.brandId
        },
        vm: this,
        ignoreStored: forceReload
      }
    );
  }

  public isCategoryOpen(categoryId: string) {
    return this.$_.some(
      this.opennedParentCategories,
      opennedCategoryId => opennedCategoryId === categoryId
    );
  }

  public get opennedParentCategories() {
    return this.$_.uniq(
      this.$_.flatten(
        this.$_.map(this.selectedCategories, selectedCategory => {
          return this.getParentCategories(selectedCategory.id);
        })
      )
    );
  }

  public getParentCategories(categoryId: IProductCategory["id"]) {
    let path = [];

    function dive(_data, _path, _, level) {
      for (let i = 0; i < _data.length; i++) {
        if (_data[i].id === categoryId) {
          path = _path;
        } else if (_.has(_data[i], "subcategories")) {
          dive(
            _data[i].subcategories,
            _.concat(_path, [_data[i].id]),
            _,
            level + 1
          );
        }
      }
    }

    dive(this.rootCategories, [], this.$_, 0);

    return path;
  }

  public mapCategories(
    categories: IProductCategory[],
    parentCategories,
    level
  ) {
    return this.$_.map(
      this.$_.orderBy(categories, ["order", "asc"]),
      (category: IProductCategory, index: number) => {
        return this.$_.merge(this.$_.pick(category, ["id", "name"]), {
          isOpened: this.isCategoryOpen(category.id),
          isLoading: false,
          isInitialising: false,
          isSelected: !!this.$_.find(this.selectedCategories, [
            "id",
            category.id
          ]),
          isFullyLoaded: true,
          offset: 0,
          type: SortableItems.CATEGORY,
          level: level + 1,
          index,
          parentCategories,
          items: this.$_.concat(
            [],
            this.mapCategories(
              category.subcategories || [],
              this.$_.concat(parentCategories, [
                this.$_.merge(this.$_.pick(category, ["id", "name"]), {
                  level: level + 1,
                  index
                })
              ]),
              level + 1
            )
          )
        });
      }
    );
  }

  public selectElement(item, isSelected: boolean) {
    if (isSelected) {
      const selectedItem = this.$_.merge(
        this.$_.pick(item, ["id", "name", "category_id"]),
        {
          object_type: RelatedProductsTypes.CATEGORY
        }
      );

      if (this.multipleSelect) this.selectedCategories.push(selectedItem);
      else {
        this.$set(this, "selectedCategories", [selectedItem]);
      }
    } else {
      this.selectedCategories = this.$_.filter(
        this.selectedCategories,
        selectedCategory => selectedCategory.id !== item.id
      );
    }
  }

  public get isOption() {
    return this.productTypes.includes(ProductTypes.PRODUCT_OPTION);
  }

  public get emptyResultsTitle() {
    if (this.isOption) return this.$t("_.no_product_options_categories");
    if (this.isAttribute) return this.$t("_.no_product_attributes_categories");
    return this.$t("_.no_product_categories");
  }

  public get emptyResultsMessage() {
    if (this.isOption)
      return this.$t("_sentence.no_product_option_categories_msg");
    if (this.isAttribute)
      return this.$t("_sentence.no_product_attribute_categories_msg");
    return this.$t("_sentence.no_product_categories_msg");
  }

  public get isAttribute() {
    return this.productTypes.includes(ProductTypes.PRODUCT_ATTRIBUTE);
  }

  public get contentAvailable() {
    return this.$userCan(...this.guardFuncionalities);
  }

  public get guardFuncionalities() {
    return ["list_product_categories"];
  }

  public beforeDestroy() {
    this.$store.dispatch("data/binList", {
      storeModule: DataModules.CATALOGUE_CATEGORIES,
      scope: this.rootCategoriesScope,
      vm: this
    });
  }

  @Watch("selectedCategories", { deep: true })
  public onSelectedCategoriesChange(value) {
    this.$emit("input", value);
  }
}
